import { z } from 'zod';

// Member Registration Schema - matches updated server-side validation
export const memberRegistrationSchema = z.object({
  fullName: z.string().min(1, 'Full name is required').trim().min(2, 'Full name must be at least 2 characters').max(255).regex(/^[a-zA-Z\s]+$/, 'Full name should only contain letters and spaces'),
  fatherName: z.string().min(1, "Father's name is required").trim().min(2, "Father's name must be at least 2 characters").max(255),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address').toLowerCase().trim(),
  gender: z.string().min(1, 'Gender is required').refine(v => ['male', 'female', 'other'].includes(v), 'Please select a gender'),
  age: z.string().min(1, 'Age is required').refine(v => { const n = parseInt(v); return !isNaN(n) && n >= 10 && n <= 100; }, 'Age must be between 10 and 100'),
  cnic: z.string().min(1, 'CNIC is required').trim().regex(/^\d{5}-\d{7}-\d{1}$/, 'CNIC must be in format: 12345-1234567-1'),
  phone: z.string().min(1, 'Phone number is required').refine(val => /^(\+92|92|0)?3[0-9]{9}$/.test(val.replace(/[\s-]/g, '')), 'Enter a valid Pakistani mobile number (e.g. 03001234567)'),
  address: z.string().min(1, 'Residential address is required').trim().min(5, 'Address must be at least 5 characters'),
  city: z.string().min(1, 'City is required').trim().max(100),
  occupation: z.string().min(1, 'Occupation is required').trim(),
  organization: z.string().min(1, 'Organization/Institution is required').trim(),
  qualification: z.string().min(1, 'Qualification is required').trim(),
  areasOfInterest: z.array(z.string()).min(1, 'Please select at least one area of interest'),
  maritalStatus: z.string().min(1, 'Marital status is required').refine(v => ['single', 'married'].includes(v), 'Please select marital status'),
  emergencyContactName: z.string().min(1, 'Emergency contact name is required').trim(),
  emergencyContactRelationship: z.string().min(1, 'Relationship is required').trim(),
  emergencyContactPhone: z.string().min(1, 'Emergency contact phone is required').refine(val => /^[\d\s\-\+\(\)]+$/.test(val), 'Invalid phone number'),
  declarationConsent: z.boolean().refine(v => v === true, 'You must agree to the declaration'),
  communicationConsent: z.boolean().refine(v => v === true, 'You must consent to communication'),
  password: z.string().min(1, 'Password is required').min(8, 'Password must be at least 8 characters').max(100).regex(/[A-Z]/, 'Must contain an uppercase letter').regex(/[a-z]/, 'Must contain a lowercase letter').regex(/[0-9]/, 'Must contain a number'),
  department: z.string().optional().default(''),
  studentId: z.string().optional().default(''),
  university: z.string().optional().default(''),
  profileImage: z.string().min(1, 'Profile photo is required - please upload or take a photo'),
});

// Login Schema (for both admin and member)
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .toLowerCase()
    .trim(),

  password: z
    .string()
    .min(1, 'Password is required'),
});

// Password Reset Request Schema
export const passwordResetRequestSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .toLowerCase()
    .trim(),
});

// Password Reset Schema
export const passwordResetSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(100, 'Password must not exceed 100 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),

  confirmPassword: z
    .string()
    .min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

// Email Resend Schema
export const resendVerificationSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .toLowerCase()
    .trim(),
});

// Type exports for TypeScript
export type MemberRegistrationInput = z.infer<typeof memberRegistrationSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestSchema>;
export type PasswordResetInput = z.infer<typeof passwordResetSchema>;
export type ResendVerificationInput = z.infer<typeof resendVerificationSchema>;
